import { derived, writable } from 'svelte/store';
import type { Task } from '$lib/domain/types';
import { taskRepo } from '$lib/db/repo';
import { createTombstonePayload } from '$lib/db/sync';
import { moveById } from '$lib/domain/blueprint';
import { syncEngine } from './sync';

const SELECTED_TASK_KEY = 'ff_selected_task_id';

const readSelectedTaskId = () => {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(SELECTED_TASK_KEY);
};

const persistSelectedTaskId = (id: string | null) => {
  if (typeof localStorage === 'undefined') return;
  if (id) localStorage.setItem(SELECTED_TASK_KEY, id);
  else localStorage.removeItem(SELECTED_TASK_KEY);
};

const taskOrder = (task: Task) => {
  if (typeof task.sortOrder === 'number' && Number.isFinite(task.sortOrder)) return task.sortOrder;
  const timestamp = Date.parse(task.createdAt || task.updatedAt || '');
  return Number.isFinite(timestamp) ? -timestamp : 0;
};

const sortTasks = (tasks: Task[]) => [...tasks].sort((a, b) => taskOrder(a) - taskOrder(b));

const nextTopSortOrder = (tasks: Task[]) => Math.min(0, ...tasks.map(taskOrder)) - 1;

const stampOrder = (tasks: Task[]) =>
  tasks.map((task, index) => ({
    ...task,
    sortOrder: index,
    updatedAt: new Date().toISOString()
  }));

export const selectedTaskId = writable<string | null>(readSelectedTaskId());

const createTasksStore = () => {
  const { subscribe, set, update } = writable<Task[]>([]);
  let snapshot: Task[] = [];
  let selectedId: string | null = null;
  subscribe((items) => {
    snapshot = items;
  });
  selectedTaskId.subscribe((id) => {
    selectedId = id;
  });

  const load = async () => {
    const tasks = await taskRepo.list();
    set(sortTasks(tasks));
    if (selectedId && !tasks.some((task) => task.id === selectedId && task.status === 'open')) {
      select(null);
    }
  };

  const add = async (title: string, project?: string) => {
    const trimmed = title.trim();
    if (!trimmed) return;
    const now = new Date().toISOString();
    const task: Task = {
      id: crypto.randomUUID(),
      title: trimmed,
      status: 'open',
      project: project?.trim() || undefined,
      sortOrder: nextTopSortOrder(snapshot),
      createdAt: now,
      updatedAt: now
    };
    await taskRepo.upsert(task);
    await syncEngine.enqueueChange('tasks', task.id, task);
    update((items) => sortTasks([task, ...items]));
  };

  const edit = async (id: string, title: string, project?: string) => {
    const current = snapshot.find((task) => task.id === id);
    const trimmed = title.trim();
    if (!current || !trimmed) return;
    const updatedTask: Task = {
      ...current,
      title: trimmed,
      project: project?.trim() || undefined,
      updatedAt: new Date().toISOString()
    };
    await taskRepo.upsert(updatedTask);
    await syncEngine.enqueueChange('tasks', updatedTask.id, updatedTask);
    update((items) => sortTasks(items.map((task) => (task.id === id ? updatedTask : task))));
  };

  const toggle = async (id: string) => {
    const current = snapshot.find((task) => task.id === id);
    if (!current) return;
    const updatedTask: Task = {
      ...current,
      status: current.status === 'done' ? 'open' : 'done',
      updatedAt: new Date().toISOString()
    };

    try {
      await taskRepo.upsert(updatedTask);
      await syncEngine.enqueueChange('tasks', updatedTask.id, updatedTask);
      update((items) => sortTasks(items.map((task) => (task.id === id ? updatedTask : task))));
      if (updatedTask.status === 'done' && selectedId === id) select(null);
    } catch (error) {
      await load();
      throw error;
    }
  };

  const remove = async (id: string) => {
    await taskRepo.remove(id);
    await syncEngine.enqueueChange('tasks', id, createTombstonePayload(id));
    update((items) => items.filter((task) => task.id !== id));
    if (selectedId === id) select(null);
  };

  const select = (id: string | null) => {
    if (id && !snapshot.some((task) => task.id === id && task.status === 'open')) return;
    selectedTaskId.set(id);
    persistSelectedTaskId(id);
  };

  const persistOrderedTasks = async (items: Task[]) => {
    const ordered = stampOrder(items);
    await Promise.all(
      ordered.map(async (task) => {
        await taskRepo.upsert(task);
        await syncEngine.enqueueChange('tasks', task.id, task);
      })
    );
    set(ordered);
  };

  const move = async (activeId: string, overId: string) => {
    const reordered = moveById(snapshot, activeId, overId);
    if (reordered === snapshot) return;
    await persistOrderedTasks(reordered);
  };

  const defer = async (id: string) => {
    const current = snapshot.find((task) => task.id === id);
    if (!current) return;
    const reordered = [...snapshot.filter((task) => task.id !== id), { ...current, status: 'open' as const }];
    if (selectedId === id) select(null);
    await persistOrderedTasks(reordered);
  };

  return { subscribe, load, add, edit, toggle, remove, select, move, defer };
};

export const tasksStore = createTasksStore();

export const selectedTask = derived([tasksStore, selectedTaskId], ([$tasks, $selectedTaskId]) =>
  $selectedTaskId ? $tasks.find((task) => task.id === $selectedTaskId && task.status === 'open') ?? null : null
);
